import { useCallback, useRef } from 'react'
import { useCurrentUser } from '@/features/auth'
import { useLocalizedNavigate } from '@/shared/hooks'
import { PrecadrageLayout, PrecadrageForm, type PrecadrageFormRef } from '../components'
import { useCreateProject } from '../hooks'

/**
 * Page for authenticated users to create a new project
 * Uses the same form as the onboarding but creates project directly
 * Back button is now in the PrecadrageLayout header (always visible)
 */
export function NewProjectPage() {
  const navigate = useLocalizedNavigate()
  const { canCreateProject, isLoading: isUserLoading } = useCurrentUser()
  const { createProject, isCreating, error } = useCreateProject()
  const formRef = useRef<PrecadrageFormRef>(null)

  // Handle field injection from AI assistant
  const handleInjectField = useCallback((field: string, content: string) => {
    formRef.current?.injectField(field, content)
  }, [])

  // Show loading while checking user permissions
  if (isUserLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  // Redirect if user cannot create projects (viewer role)
  if (!canCreateProject) {
    navigate('/dashboard', { replace: true })
    return null
  }

  return (
    <PrecadrageLayout onInjectField={handleInjectField}>
      <div className="flex flex-1 flex-col overflow-y-auto">
        {/* Form - back button is now in the header */}
        <div className="flex flex-1 items-start justify-center pt-8">
          <div className="w-full max-w-[560px]">
            <PrecadrageForm
              ref={formRef}
              onSubmit={createProject}
              isSubmitting={isCreating}
              error={error}
            />
          </div>
        </div>
      </div>
    </PrecadrageLayout>
  )
}
